import { useAderenciaPrecosStore } from '../AderenciaPrecos.store';
import { useDebounce } from '../../../../../hooks';
import * as service from '../services';

interface FilterItem {
  value: string;
  label: string;
}

type ProductItem = {
  ean: string;
  descricao: string;
};

export const useAderenciaProductFilter = () => {
  const { setAderenciaPrecosState, setAderenciaBodyRequests } = useAderenciaPrecosStore();
  const debounce = useDebounce();

  const updateProductList = (data: ProductItem[], isBulk = false) => {
    const state = useAderenciaPrecosStore.getState();
    const currentProduto = state.produto;

    const list = data?.map(item => ({
      value: item.ean,
      label: `${item.ean} - ${item.descricao}`,
    }));

    if (isBulk) {
      setAderenciaPrecosState('produto', { ...currentProduto, list: list, cache: list });
    } else {
      if (currentProduto.value?.length) {
        const removeDuplicate = list.filter(item => !currentProduto.value?.includes(item.value));
        const concatList = (currentProduto.cache || []).concat(removeDuplicate);
        setAderenciaPrecosState('produto', { ...currentProduto, list: concatList });
      } else {
        setAderenciaPrecosState('produto', { ...currentProduto, list });
      }
    }
  };

  const getProducts = async (query: string | string[], isBulk = false) => {
    try {
      const response = await service.getAderenciaPrecosFiltros({ campo: 'PRODUTO', query });

      if (response?.status === 200) {
        updateProductList(response.data?.content ?? [], isBulk);
      }
    } catch (error) {
      console.error(`Erro ao buscar filtros para produto:`, error);
    }
  };

  const applyCopyPasteEansToProductFilter = (value: string) => {
    const state = useAderenciaPrecosStore.getState();

    const eans = [...new Set(value.split(' '))];
    const currentValues = (state.produto.value || []).map(String);
    const newValue = [...new Set([...currentValues, ...eans])];

    setAderenciaPrecosState('produto', { ...state.produto, value: newValue });
    getProducts(newValue, true);
  };

  const handleAderenciaProductFilterSearch = (value: string) => {
    if (value && typeof value === 'string' && value.match(/^\d+ \d+[\d+ ]*$/)) {
      applyCopyPasteEansToProductFilter(value);
    } else {
      debounce(() => {
        getProducts([value]);
      });
    }
  };

  const addProduct = (item: FilterItem) => {
    const state = useAderenciaPrecosStore.getState();
    const currentValues = (state.produto.value || []).map(String);

    if (!currentValues.includes(item.value)) {
      const value = [...currentValues, item.value];
      const cache = [...(state.produto.cache || []), item];
      setAderenciaPrecosState('produto', { ...state.produto, value, cache });
      setAderenciaBodyRequests('produto', value);
    }
  };

  const removeProduct = (item: FilterItem) => {
    const state = useAderenciaPrecosStore.getState();
    const value = (state.produto.value || []).map(String).filter(value => value !== item.value);
    const cache = state.produto.cache?.filter(cache => cache.value !== item.value);
    setAderenciaPrecosState('produto', { ...state.produto, value, cache });
    setAderenciaBodyRequests('produto', value);
  };

  const handleSelectAderenciaProductFilter = (item: FilterItem) => {
    const state = useAderenciaPrecosStore.getState();
    if ((state.produto.value || []).map(String).includes(item.value)) {
      removeProduct(item);
    } else {
      addProduct(item);
    }
  };

  const handleCleanAderenciaProductFilter = () => {
    const state = useAderenciaPrecosStore.getState();
    setAderenciaPrecosState('produto', { ...state.produto, value: [], cache: [] });
    setAderenciaBodyRequests('produto', []);
  };

  return {
    handleAderenciaProductFilterSearch,
    handleCleanAderenciaProductFilter,
    handleSelectAderenciaProductFilter,
    applyCopyPasteEansToProductFilter,
  };
};
